import { Injectable, inject } from '@angular/core';
import {HttpClient, HttpContext, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {isOdinBusinessId} from './business.service';
import {RAW_HTTP_ERROR} from '../interceptors/auth.interceptor';
import {CreatedId} from '../model/store';
import {
  ClientAccountType,
  ClientStaffRequest,
  ClientStaffResponse,
  CompanyRole,
  EmployeeRequest,
  EmployeeResponse,
  EmployeeRolesResponse,
  GrantableRolesResponse,
  PageResponse,
} from '../model/identity';

export interface PageQuery {
  pageToken?: string | null
  pageSize?: number
}

/**
 * identity company (admin) API, ticket §4. one class, every route, nothing else.
 *
 * Every call opts out of auth.interceptor's error flattening (RAW_HTTP_ERROR): callers get the
 * HttpErrorResponse with status + ErrorResponse body, so 409 → username field, 400 → errors[].field,
 * 403 → server reason. Without it the interceptor treats a 403 like a 401 (refresh + silent
 * re-send) and reduces every body to one string (plan §2.1).
 *
 * `businessId` on the client routes is the MERCURY id (plan §2.2); resolve it from Go Live
 * before calling. Errors are never swallowed here.
 */
@Injectable({
  providedIn: 'root'
})
export class IdentityAdminService {
  private http = inject(HttpClient);

  /** legacy client accounts (a business with a Firestore id) still live here; see accountsUrl */
  private readonly baseUrl = environment.baseUrl + '/identity/company/v1'
  /** employees, metadata and the lifecycle verbs: v3 since the v3 sign-in (task-login-employee-v3, 2026-09-04) */
  private readonly v3Url = environment.baseUrl + '/identity/company/v3'

  // ---- §4.1 --------------------------------------------------------------------------------

  getGrantableRoles(): Observable<GrantableRolesResponse> {
    return this.http.get<GrantableRolesResponse>(`${this.v3Url}/metadata/roles`, this.opts())
  }

  // ---- §4.2–4.5 employees ---------------------------------------------------------------------

  listEmployees(page: PageQuery = {}): Observable<PageResponse<EmployeeResponse>> {
    return this.http.get<PageResponse<EmployeeResponse>>(`${this.v3Url}/employees`, this.opts(page))
  }

  getEmployee(id: string): Observable<EmployeeResponse> {
    return this.http.get<EmployeeResponse>(`${this.v3Url}/employees/${id}`, this.opts())
  }

  createEmployee(req: EmployeeRequest): Observable<CreatedId> {
    return this.http.post<CreatedId>(`${this.v3Url}/employees`, req, this.opts())
  }

  /** replaces the whole role list */
  updateEmployeeRoles(id: string, roles: CompanyRole[]): Observable<EmployeeRolesResponse> {
    return this.http.put<EmployeeRolesResponse>(`${this.v3Url}/employees/${id}/roles`, {roles}, this.opts())
  }

  // ---- v3 lifecycle verbs (api-specs §10.2, §12); target strictly below the caller ----------------

  /** deactivate (sign-in refused, username kept) or reactivate. 403 at or above the caller's rank */
  setEmployeeActive(id: string, active: boolean): Observable<EmployeeResponse> {
    return this.http.put<EmployeeResponse>(`${this.v3Url}/employees/${id}/active`, {active}, this.opts())
  }

  /** set or reset without proof; identity never returns it, so the caller shows it once */
  setEmployeePassword(id: string, password: string): Observable<EmployeeResponse> {
    return this.http.put<EmployeeResponse>(`${this.v3Url}/employees/${id}/password`, {password}, this.opts())
  }

  /**
   * 204, and 204 again for an id already gone. Frees the username. Refused 403 below EXECUTIVE or
   * at/above the caller's rank, 409 EMPLOYEE_STILL_ACTIVE on a live account: deactivate first.
   */
  deleteEmployee(id: string): Observable<void> {
    return this.http.delete<void>(`${this.v3Url}/employees/${id}`, this.opts())
  }

  // ---- §4.6–4.9 client accounts (businessId = mercury id) ------------------------------------

  listClientAccounts(
    businessId: string,
    query: PageQuery & { accountType?: ClientAccountType } = {}
  ): Observable<PageResponse<ClientStaffResponse>> {
    return this.http.get<PageResponse<ClientStaffResponse>>(this.accountsUrl(businessId), this.opts(query))
  }

  getClientAccount(businessId: string, staffId: string): Observable<ClientStaffResponse> {
    return this.http.get<ClientStaffResponse>(`${this.accountsUrl(businessId)}/${staffId}`, this.opts())
  }

  /**
   * do NOT send `id`; body.businessId may be omitted or must equal the path.
   * For a V3-born business prefer mercury `POST /mercury/v3/staff` (MercuryStaffV3Service), which
   * calls this route itself and also writes the employment rows; call this directly only for
   * TERMINAL_ACCOUNT logins and legacy businesses.
   */
  createClientAccount(businessId: string, req: ClientStaffRequest): Observable<CreatedId> {
    return this.http.post<CreatedId>(this.accountsUrl(businessId), req, this.opts())
  }

  /**
   * v3 only (a legacy business has no such verb): `false` walls the login off (sign-in, refresh and
   * switch-location all refuse), the username stays reserved and mercury's employment rows stay
   * pointed at it. This is the "delete" for a V3-born business until mercury ships staff termination.
   */
  setClientAccountActive(businessId: string, staffId: string, active: boolean): Observable<void> {
    return this.http.put<void>(`${this.accountsUrl(businessId)}/${staffId}/active`, {active}, this.opts())
  }

  /** hard delete. 200, empty body. no undo. */
  deleteClientAccount(businessId: string, staffId: string): Observable<void> {
    return this.http.delete<void>(`${this.accountsUrl(businessId)}/${staffId}`, this.opts())
  }

  /**
   * identity keeps two stores that do not see each other: a business born in V3 (UUID id, minted
   * by moneta) lives in MongoDB behind `/company/v3`; a legacy business lives in Firestore behind
   * `/company/v1`. Same verbs, same shapes (v3 adds `credentialed`, drops `storeId`).
   */
  private accountsUrl(businessId: string): string {
    const version = isOdinBusinessId(businessId) ? 'v3' : 'v1'
    return `${environment.baseUrl}/identity/company/${version}/clients/${businessId}/accounts`
  }

  // ---- internals ------------------------------------------------------------------------------

  /** fresh context per request; undefined / null / '' query values are dropped */
  private opts(query: object = {}) {
    let params = new HttpParams()
    for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
      if (v === undefined || v === null || v === '') continue
      params = params.set(k, String(v))
    }
    return {params, context: new HttpContext().set(RAW_HTTP_ERROR, true)}
  }
}
